// CardSprite - Phaser game object for card display
import Phaser from 'phaser';
import type { Card, CardDefinition, CardOrientation, EffectKeyword } from '../../types';
import { getLogger } from '../../utils/logger';

const log = getLogger('CardSprite');

// Effect abbreviations for display
const EFFECT_ABBREVIATIONS: Partial<Record<EffectKeyword, string>> = {
  pierce: '貫',
  ranged: '遠',
  armorBreak: '甲',
  slide: '滑',
  ambush: '伏',
  multiAttack: '連',
  block: '擋',
  cover: '護',
  electrify: '電',
  taunt: '嘲',
  stealth: '隱',
  protect: '盾',
  poison: '毒',
  dismantle: '解',
  freeze: '凍',
  burn: '燃',
  stun: '暈',
  mechaMerge: '合',
  devourMerge: '噬',
  parasiteMerge: '寄',
  stackMerge: '疊',
  reward: '賞',
  charge: '充',
  drain: '吸',
  overload: '填',
  magicResist: '抗',
  destroyResist: '固',
  effectResist: '免',
  bind: '縛',
  tap: '置',
  decay: '腐',
  silence: '默',
  board: '乘',
  transport: '運',
  soul: '魂',
  revive: '生',
  haunt: '纏',
  transform: '變',
  aura: '環',
  summon: '召',
};

export interface CardSpriteConfig {
  scene: Phaser.Scene;
  x: number;
  y: number;
  card: Card;
  definition: CardDefinition;
  scale?: number;
  faceDown?: boolean;
  interactive?: boolean;
}

export class CardSprite extends Phaser.GameObjects.Container {
  public readonly cardId: string;
  public readonly definitionId: string;
  private cardData: Card;
  private cardDef: CardDefinition;
  private background: Phaser.GameObjects.Rectangle;
  private cardImage: Phaser.GameObjects.Image | null = null;
  private faceDownText: Phaser.GameObjects.Text | null = null;
  private faceDown: boolean;
  private glowEffect: Phaser.GameObjects.Rectangle | null = null;
  private atkBadge: Phaser.GameObjects.Container | null = null;
  private scoreBadge: Phaser.GameObjects.Container | null = null;
  private rarityBorder: Phaser.GameObjects.Rectangle | null = null;
  private tappedOverlay: Phaser.GameObjects.Rectangle | null = null;


  // Card dimensions (optimized for 1920x1080) - matches actual card aspect ratio
  static readonly CARD_WIDTH = 100;
  static readonly CARD_HEIGHT = 140;

  constructor(config: CardSpriteConfig) {
    super(config.scene, config.x, config.y);

    this.cardId = config.card.id;
    this.definitionId = config.card.definitionId;
    this.cardData = config.card;
    this.cardDef = config.definition;
    this.faceDown = config.faceDown ?? false;

    const scale = config.scale ?? 1;
    const width = CardSprite.CARD_WIDTH * scale;
    const height = CardSprite.CARD_HEIGHT * scale;

    // Background (only visible when no image or face down)
    this.background = config.scene.add.rectangle(0, 0, width, height, this.getCardColor());
    this.background.setStrokeStyle(2, 0x444466);
    this.add(this.background);

    if (this.faceDown) {
      // Face down card - show card back design
      this.faceDownText = config.scene.add.text(0, 0, '?', {
        fontSize: `${String(40 * scale)}px`,
        color: '#666688',
        fontStyle: 'bold',
      });
      this.faceDownText.setOrigin(0.5, 0.5);
      this.add(this.faceDownText);
    } else if (config.definition.imagePath && config.scene.textures.exists(config.definition.id)) {
      // Face up with image - display full card image (no extra UI needed)
      try {
        this.cardImage = config.scene.add.image(0, 0, config.definition.id);
        // Scale image to fill the entire card area
        this.cardImage.setDisplaySize(width, height);
        this.add(this.cardImage);
        // Hide background when we have an image
        this.background.setVisible(false);
      } catch {
        // 圖片載入失敗 - 顯示 fallback 佔位圖
        log.warn(`圖片載入失敗: ${config.definition.id}`);
        this.createFallbackPlaceholder(config.scene, width, height);
      }
    } else {
      // Face up but no image - show placeholder with card name
      const nameText = config.scene.add.text(0, 0, this.cardDef.nameZh || this.cardDef.name, {
        fontSize: `${String(12 * scale)}px`,
        color: '#ffffff',
        fontStyle: 'bold',
        align: 'center',
        wordWrap: { width: width - 8 },
      });
      nameText.setOrigin(0.5, 0.5);
      this.add(nameText);
    }

    // Apply orientation
    this.updateOrientation();

    // Add effect badges (for face-up cards on battlefield)
    if (!this.faceDown && config.definition.effectIds && config.definition.effectIds.length > 0) {
      this.createEffectBadges(config.scene, config.definition.effectIds as EffectKeyword[], scale);
    }

    // Add ATK badge (for units)
    if (!this.faceDown && config.definition.type === 'unit' && config.definition.atk !== undefined) {
      this.createAtkBadge(config.scene, config.definition.atk, scale);
    }

    // Add Score badge (for units)
    if (!this.faceDown && config.definition.type === 'unit' && config.definition.score !== undefined) {
      this.createScoreBadge(config.scene, config.definition.score, scale);
    }

    // 稀有度邊框（依據 rarity 顯示不同顏色）
    if (!this.faceDown && config.definition.rarity) {
      this.createRarityBorder(config.scene, config.definition.rarity, scale);
    }

    // Make interactive if needed
    if (config.interactive) {
      this.setSize(width, height);
      this.setInteractive({ useHandCursor: true });
    }

    // Add to scene
    config.scene.add.existing(this);
  }

  // Keyword color mapping
  private static readonly KEYWORD_COLORS: Partial<Record<EffectKeyword, number>> = {
    poison: 0x44cc44,
    burn: 0xff6622,
    freeze: 0x44ccff,
    stun: 0xffcc00,
    pierce: 0xff4444,
    ranged: 0x88aaff,
    stealth: 0x888888,
    taunt: 0xcc8844,
    protect: 0x4488ff,
    drain: 0xcc44cc,
    silence: 0xaaaaaa,
    revive: 0x44ff88,
    soul: 0xaa88ff,
    transform: 0xff88ff,
  };

  private createEffectBadges(scene: Phaser.Scene, effects: EffectKeyword[], scale: number): void {
    if (effects.length === 0) return;

    const width = CardSprite.CARD_WIDTH * scale;
    const height = CardSprite.CARD_HEIGHT * scale;

    const container = scene.add.container(-width / 2 + 4, -height / 2 + 4);

    // Show up to 4 effect badges as colored dots with abbreviation
    const displayEffects = effects.slice(0, 4);
    const badgeSize = 14 * scale;

    displayEffects.forEach((effect, index) => {
      const firstChar = effect[0];
      const abbrev = EFFECT_ABBREVIATIONS[effect] ?? (firstChar ? firstChar.toUpperCase() : '?');
      const color = CardSprite.KEYWORD_COLORS[effect] ?? 0xffff00;

      // Colored circle dot
      const dot = scene.add.circle(
        index * (badgeSize + 3) + badgeSize / 2,
        badgeSize / 2,
        badgeSize / 2,
        color,
        0.85
      );
      dot.setStrokeStyle(1, 0x000000, 0.6);
      container.add(dot);

      // Abbreviation text
      const text = scene.add.text(
        index * (badgeSize + 3) + badgeSize / 2,
        badgeSize / 2,
        abbrev,
        {
          fontSize: `${String(Math.floor(9 * scale))}px`,
          color: '#ffffff',
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 1,
        }
      );
      text.setOrigin(0.5, 0.5);
      container.add(text);
    });

    this.add(container);
  }

  private createAtkBadge(scene: Phaser.Scene, atk: number, scale: number): void {
    const width = CardSprite.CARD_WIDTH * scale;
    const height = CardSprite.CARD_HEIGHT * scale;

    this.atkBadge = scene.add.container(width / 2 - 16 * scale, height / 2 - 16 * scale);

    // ATK background circle
    const bg = scene.add.circle(0, 0, 12 * scale, 0xff0000, 0.9);
    bg.setStrokeStyle(2, 0xffffff);
    this.atkBadge.add(bg);

    // ATK text
    const text = scene.add.text(0, 0, String(atk), {
      fontSize: `${String(Math.floor(14 * scale))}px`,
      color: '#ffffff',
      fontStyle: 'bold',
    });
    text.setOrigin(0.5, 0.5);
    this.atkBadge.add(text);

    this.add(this.atkBadge);
  }

  private createScoreBadge(scene: Phaser.Scene, score: number, scale: number): void {
    const width = CardSprite.CARD_WIDTH * scale;
    const height = CardSprite.CARD_HEIGHT * scale;

    this.scoreBadge = scene.add.container(-width / 2 + 16 * scale, height / 2 - 16 * scale);

    // Score background (blue star shape approximated with circle)
    const bg = scene.add.circle(0, 0, 12 * scale, 0x2244aa, 0.9);
    bg.setStrokeStyle(1.5, 0x88aaff);
    this.scoreBadge.add(bg);

    // Star icon (tiny, offset)
    const starIcon = scene.add.text(0, -1 * scale, '★', {
      fontSize: `${String(Math.floor(7 * scale))}px`,
      color: '#ffdd44',
    }).setOrigin(0.5, 1);
    this.scoreBadge.add(starIcon);

    // Score text
    const text = scene.add.text(0, 3 * scale, String(score), {
      fontSize: `${String(Math.floor(11 * scale))}px`,
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0);
    this.scoreBadge.add(text);

    this.add(this.scoreBadge);
  }

  /**
   * 圖片載入失敗時的 fallback 佔位圖
   */
  private createFallbackPlaceholder(scene: Phaser.Scene, width: number, height: number): void {
    // 背景色保持可見
    this.background.setVisible(true);
    this.background.setFillStyle(0x333344);

    // 佔位圖示
    const icon = scene.add.text(0, -10, '🎴', {
      fontSize: `${String(Math.floor(width * 0.3))}px`,
    }).setOrigin(0.5);
    this.add(icon);

    // 卡名
    const nameText = scene.add.text(0, height * 0.25, this.cardDef.nameZh || this.cardDef.name, {
      fontSize: `${String(Math.max(8, Math.floor(width * 0.1)))}px`,
      color: '#aaaaaa',
      fontStyle: 'bold',
      align: 'center',
      wordWrap: { width: width - 8 },
    }).setOrigin(0.5);
    this.add(nameText);
  }

  /**
   * 根據稀有度建立邊框
   * N(普通): 灰色, R(稀有): 藍色, SR(史詩): 紫色, SSR/SSSR(傳說): 金色 + shimmer
   */
  private createRarityBorder(scene: Phaser.Scene, rarity: string, scale: number): void {
    const borderColors: Record<string, number> = {
      N: 0x666666,
      R: 0x4488ff,
      SR: 0xaa44ff,
      SSR: 0xffd700,
      SSSR: 0xffd700,
    };

    const color = borderColors[rarity] ?? 0x666666;
    const borderWidth = rarity === 'N' ? 1 : 2;
    const width = CardSprite.CARD_WIDTH * scale;
    const height = CardSprite.CARD_HEIGHT * scale;

    // Outer glow for R+ rarity
    if (rarity !== 'N') {
      const glowBorder = scene.add.rectangle(0, 0, width + 8, height + 8);
      glowBorder.setStrokeStyle(4, color, 0.2);
      glowBorder.setFillStyle(0x000000, 0);
      this.addAt(glowBorder, 0);

      if (rarity === 'SSR' || rarity === 'SSSR') {
        scene.tweens.add({
          targets: glowBorder,
          alpha: { from: 0.3, to: 0.8 },
          duration: 700,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      } else if (rarity === 'SR') {
        scene.tweens.add({
          targets: glowBorder,
          alpha: { from: 0.4, to: 0.7 },
          duration: 1100,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    }

    // Main rarity border
    this.rarityBorder = scene.add.rectangle(0, 0, width + 4, height + 4);
    this.rarityBorder.setStrokeStyle(borderWidth, color);
    this.rarityBorder.setFillStyle(0x000000, 0);
    this.addAt(this.rarityBorder, rarity === 'N' ? 0 : 1);
  }

  private getCardColor(): number {
    if (this.faceDown) return 0x2a2a4a;

    const factionColors: Record<string, number> = {
      neutral_spell: 0x666666,
      neutral_unit: 0x555555,
      power_beast: 0xffaa00,
      carbon: 0x333333,
      fairy: 0x88ff88,
      artwork: 0xdd88dd,
      traveler: 0x8888ff,
      savage: 0x884422,
      orc: 0x448844,
      cat: 0xffcc88,
      candy: 0xff88cc,
      robot: 0x888899,
      skeleton: 0xcccccc,
      steam: 0x996633,
      goat: 0xddddaa,
      monster: 0x8844aa,
      virtual: 0x44dddd,
      hero: 0xffdd44,
      island: 0x44aaff,
      circus: 0xff4488,
    };

    return factionColors[this.cardDef.faction] ?? 0x444466;
  }

  public updateOrientation(): void {
    const orientation = this.cardData.orientation;
    // Remove old tapped overlay
    if (this.tappedOverlay) {
      this.tappedOverlay.destroy();
      this.tappedOverlay = null;
    }

    switch (orientation) {
      case 'tilted':
        this.setAngle(15);
        this.setAlpha(1);
        break;
      case 'tapped':
        this.setAngle(90);
        // Add darkening overlay for tapped cards
        this.tappedOverlay = this.scene.add.rectangle(
          0, 0,
          CardSprite.CARD_WIDTH, CardSprite.CARD_HEIGHT,
          0x000000, 0.35
        );
        this.add(this.tappedOverlay);
        break;
      default:
        this.setAngle(0);
        this.setAlpha(1);
    }
  }

  public setCardOrientation(orientation: CardOrientation): void {
    this.cardData = { ...this.cardData, orientation };
    this.updateOrientation();
  }

  public highlight(color: number = 0x00ff00): void {
    if (this.glowEffect) {
      this.glowEffect.destroy();
    }
    this.glowEffect = this.scene.add.rectangle(
      0, 0,
      CardSprite.CARD_WIDTH + 8,
      CardSprite.CARD_HEIGHT + 8,
      color,
      0.3
    );
    this.addAt(this.glowEffect, 0);
  }

  public clearHighlight(): void {
    if (this.glowEffect) {
      this.glowEffect.destroy();
      this.glowEffect = null;
    }
  }

  public setSelected(selected: boolean): void {
    if (selected) {
      this.highlight(0x00ff00);
      this.setScale(1.1);
    } else {
      this.clearHighlight();
      this.setScale(1);
    }
  }

  public flipCard(faceDown: boolean): void {
    this.faceDown = faceDown;
    this.background.setFillStyle(this.getCardColor());
    // Note: Full card flip would require recreating the sprite
    // For now, just update the background color
  }

  public getCard(): Card {
    return this.cardData;
  }

  public getDefinition(): CardDefinition {
    return this.cardDef;
  }

}
